import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { feedAPI, followsAPI, resolveImageUrl } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Rss, ArrowLeft, ArrowRight, Storefront, Users } from '@phosphor-icons/react';
import { stripHtmlToText } from '../utils/text';
import { profileLink } from '../utils/profile';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function FeedPage() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeedData();
  }, []);

  const loadFeedData = async () => {
    try {
      const [feedRes, followingRes] = await Promise.all([
        feedAPI.getFeed(50),
        followsAPI.getFollowing()
      ]);
      setFeed(feedRes.data);
      setFollowing(followingRes.data);
    } catch (err) {
      console.error('Failed to load feed:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-[#999999] hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Your Feed
            </h1>
            <p className="text-[#999999] mt-1">Products from your preferred vendors</p>
          </div>
          <Link to="/explore">
            <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="discover-vendors-button">
              <Users className="w-4 h-4 mr-2" />
              Discover Vendors
            </Button>
          </Link>
        </div>

        {/* Following Preview */}
        {following.length > 0 && (
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-6">
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm font-semibold text-[#999999] uppercase tracking-wider">
                Following ({following.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-2">
              <div className="flex gap-3 overflow-x-auto pb-2">
                {following.map((vendor) => (
                  <Link
                    key={vendor.user_id}
                    to={profileLink(vendor)}
                    className="flex-shrink-0 flex flex-col items-center gap-2 p-3 bg-[#111111] rounded-sm hover:bg-[#222222] transition-colors"
                  >
                    {vendor.avatar_url ? (
                      <img src={vendor.avatar_url} alt={vendor.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-[#22C55E]/20 rounded-full flex items-center justify-center text-[#22C55E] font-bold">
                        {vendor.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="text-xs text-white font-medium truncate max-w-[80px]">{vendor.name}</span>
                    {vendor.username && (
                      <span className="text-xs text-[#22C55E]">${vendor.username}</span>
                    )}
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feed */}
        {loading ? (
          <div className="text-center py-12 text-[#999999]">Loading...</div>
        ) : feed.length === 0 ? (
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none">
            <CardContent className="p-12 text-center">
              <Rss className="w-16 h-16 mx-auto mb-4 text-[#333333]" />
              <p className="text-white font-semibold mb-2">Your feed is empty</p>
              <p className="text-[#999999] mb-6">Follow vendors to see their products here</p>
              <Link to="/explore">
                <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">
                  Explore Vendors
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {feed.map((product) => (
              <Card 
                key={product.product_id}
                className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none hover:-translate-y-1 hover:border-[#22C55E] transition-all duration-200 overflow-hidden"
                data-testid={`feed-product-${product.product_id}`}
              >
                {product.image_url && (
                  <div className="aspect-video bg-[#111111] overflow-hidden">
                    <img 
                      src={resolveImageUrl(product.image_url)} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.className = 'hidden'; }}
                    />
                  </div>
                )}
                <CardContent className="p-4">
                  {/* Seller info */}
                  <Link to={profileLink(product.seller)} className="flex items-center gap-2 mb-3 hover:opacity-80">
                    {product.seller?.avatar_url ? (
                      <img src={product.seller.avatar_url} alt={product.seller.name} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 bg-[#22C55E]/20 rounded-full flex items-center justify-center text-[#22C55E] font-bold text-xs">
                        {product.seller?.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="text-sm text-[#999999]">{product.seller?.name}</span>
                    {product.seller?.username && (
                      <span className="text-xs text-[#22C55E]">${product.seller.username}</span>
                    )}
                  </Link>
                  
                  <span className="text-xs bg-[#333333] px-2 py-1 rounded-sm text-[#999999]">
                    {product.category}
                  </span>
                  
                  <h3 className="font-bold text-white mt-2 mb-1 line-clamp-1" style={{ fontFamily: 'Cabinet Grotesk' }}>
                    {product.name}
                  </h3>
                  <p className="text-sm text-[#999999] mb-3 line-clamp-2">
                    {stripHtmlToText(product.description)}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <p className="text-xl font-bold text-[#22C55E]" style={{ fontFamily: 'Cabinet Grotesk' }}>
                      {formatCurrency(product.price)}
                    </p>
                    <Link to={`/product/${product.product_id}`}>
                      <Button size="sm" className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm text-xs">
                        View
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
